// NOTE: based on https://github.com/davidjerleke/embla-carousel/tree/v6.2.0/packages/embla-carousel-autoplay/src

export const defaultOptions = {
  defaultDelay: 4000,
  delayFn: null,
  playOnInit: true,
  stopOnInteraction: true,
  pauseOnMouseEnter: true,
  stopOnLastSnap: false,
};

/**
 * Customzed Autoplay Plugin for Embla Carousel.
 * @param {Object} userOptions - configuration options.
 * @param {HTMLElement} userNode - custom root element.
 * @returns {Object} plugin instance.
 */
function Autoplay(userOptions, userNode) {
  const options = {
    ...defaultOptions,
    ...Autoplay.globalOptions,
    ...userOptions,
  };
  const { playOnInit, stopOnInteraction, pauseOnMouseEnter, stopOnLastSnap } = options;
  let carousel = null;
  let timer = 0;

  function destroy() {
    if (stopOnInteraction) {
      carousel.off('pointerDown', destroy);
    }
    stop();
    timer = 0;
  }

  function interaction() {
    if (stopOnInteraction) {
      destroy();
    }
  }

  function stop() {
    if (!timer) {
      return;
    }
    window.clearTimeout(timer);
  }

  function play() {
    stop();
    let millis = typeof options.delayFn === 'function' ? options.delayFn(carousel) : options.defaultDelay;

    millis = millis > 0 ? millis : options.defaultDelay;
    millis = millis > 0 ? millis : 500;

    timer = window.setTimeout(next, millis);
  }

  function reset() {
    if (!timer) {
      return;
    }
    stop();
    play();
  }

  function next() {
    const { index } = carousel.internalEngine();
    const kill = stopOnLastSnap && index.get() === index.max;

    if (kill) {
      return destroy();
    }

    if (carousel.canScrollNext()) {
      carousel.scrollNext();
    } else {
      carousel.scrollTo(0);
    }

    return play();
  }

  function init(embla) {
    carousel = embla;
    const { eventStore } = carousel.internalEngine();
    const emblaRoot = carousel.rootNode();
    const root = (userNode && userNode(emblaRoot)) || emblaRoot;

    if (stopOnInteraction) {
      carousel.on('pointerDown', interaction);
    }

    if (pauseOnMouseEnter) {
      eventStore.add(root, 'mouseenter', stop);
      eventStore.add(root, 'mouseleave', reset);
    }

    eventStore.add(document, 'visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        return stop();
      }

      return reset();
    });
    eventStore.add(window, 'pagehide', (event) => {
      if (event.persisted) {
        stop();
      }
    });

    if (playOnInit) {
      play();
    }
  }

  return {
    name: 'Autoplay',
    options,
    init,
    destroy,
    play,
    stop,
    reset,
    interaction,
  };
}

Autoplay.globalOptions = undefined;

export default Autoplay;
